import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../AuthContext';
import { C, S } from '../styles';
import { DropZone } from '../components/DropZone';

const EWN_WORDING = `EARLY WARNING NOTICE

This document serves as an Early Warning Notice pursuant to the contract conditions. The Contractor hereby gives notice of a matter which could:
(a) increase the total of the Prices,
(b) delay Completion or a Key Date,
(c) delay meeting a Condition for a bonus, or
(d) impair the performance of the works in use.

The Subcontractor reserves all rights and entitlements under the subcontract. This notice is issued without prejudice to any other rights and remedies available. The works described herein fall outside the agreed subcontract scope and will require separate assessment and valuation.`;

const CVI_WORDING = `CONFIRMATION OF VERBAL INSTRUCTION

This document confirms a verbal instruction received on the date and time stated above. The Subcontractor has carried out or will carry out the instructed works under protest, reserving all rights to recover additional costs and extensions of time as appropriate.

The works described herein constitute a change to the agreed subcontract scope. The Subcontractor hereby notifies the Main Contractor that costs will be tracked and a variation account will be submitted for agreement. All additional resources deployed shall be recorded on contemporaneous daywork sheets.`;

const IR_WORDING = `INSTRUCTION RECEIVED

This records a formal instruction received from the Main Contractor. The attached document(s) constitute the instruction. Proline Internals Ltd will proceed in accordance with the instruction subject to agreement of any additional costs and/or time.`;

const IMPACT_OPTIONS = [
  { value: 'cost', label: 'Cost' },
  { value: 'programme', label: 'Programme / Time' },
  { value: 'quality', label: 'Quality' },
  { value: 'health_safety', label: 'Health & Safety' },
  { value: 'design', label: 'Design' },
];

const CVI_METHODS = [
  { value: 'Site meeting', label: 'Site meeting' },
  { value: 'Phone call', label: 'Phone call' },
  { value: 'Teams / video call', label: 'Teams / video call' },
  { value: 'Email (informal)', label: 'Email (informal)' },
  { value: 'Other', label: 'Other' },
];

const IR_METHODS = [
  { value: 'Email', label: 'Email' },
  { value: 'Drawing / Document', label: 'Drawing / Document' },
  { value: 'RFI Response', label: 'RFI Response' },
  { value: 'Letter', label: 'Letter' },
  { value: 'Other', label: 'Other' },
];

function SectionCard({ number, title, children }) {
  return (
    <div style={{ ...S.card, marginBottom: '20px' }}>
      <h2 style={{ ...S.sectionTitle, marginBottom: '16px', fontSize: '15px' }}>
        {number}. {title}
      </h2>
      {children}
    </div>
  );
}

function LegalPreview({ wording }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ ...S.card, marginBottom: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: show ? '16px' : 0 }}>
        <h2 style={{ ...S.sectionTitle, fontSize: '15px', margin: 0 }}>Legal Wording Preview</h2>
        <button type="button" onClick={() => setShow(!show)} style={{ ...S.btn, ...S.btnGhost, ...S.btnSm }}>
          {show ? 'Hide' : 'Show'} Preview
        </button>
      </div>
      {show ? (
        <div style={{
          background: '#f9fafb', border: `1px solid ${C.border}`, borderRadius: '6px',
          padding: '16px', fontSize: '13px', lineHeight: '1.7',
          color: C.textMuted, whiteSpace: 'pre-line', fontStyle: 'italic',
        }}>
          {wording}
        </div>
      ) : (
        <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: C.textMuted }}>
          The appropriate legal wording will be appended to the saved document automatically.
        </p>
      )}
    </div>
  );
}

// ─── Shared project loader hook ───────────────────────────────────────────────

function useProjects(profileId, role) {
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    async function load() {
      let query = supabase.from('projects').select('id, name, reference, project_code, main_contractor').eq('active', true).order('name');
      if (role !== 'admin') {
        const { data: pu } = await supabase.from('project_users').select('project_id').eq('user_id', profileId);
        const ids = (pu || []).map(x => x.project_id);
        if (ids.length > 0) query = query.in('id', ids);
        else { setProjects([]); return; }
      }
      const { data } = await query;
      setProjects(data || []);
    }
    if (profileId) load();
  }, [profileId, role]);
  return projects;
}

async function generateReference(projectId, type, projects) {
  const { data } = await supabase
    .from('instructions')
    .select('reference')
    .eq('project_id', projectId)
    .eq('type', type);
  const existing = (data || []).map(r => {
    const m = r.reference?.match(/(\d+)$/);
    return m ? parseInt(m[1], 10) : 0;
  });
  const next = existing.length > 0 ? Math.max(...existing) + 1 : 1;
  const project = projects.find(p => p.id === projectId);
  const code = project?.project_code || project?.reference || 'PRJ';
  return `${type}-${code}-${String(next).padStart(3, '0')}`;
}

// ─── EWN FORM ─────────────────────────────────────────────────────────────────

function EWNForm({ navigate, params, profile, projects }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [form, setForm] = useState({
    project_id: params?.projectId || '',
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    location: '',
    gridlines: '',
    level: '',
    description: '',
    potential_impact: [],
    action_requested: '',
    raised_by_name: profile?.full_name || '',
    raised_by_role: '',
  });

  function update(field, value) { setForm(f => ({ ...f, [field]: value })); }

  function toggleImpact(val) {
    setForm(f => ({
      ...f,
      potential_impact: f.potential_impact.includes(val)
        ? f.potential_impact.filter(v => v !== val)
        : [...f.potential_impact, val],
    }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setError('');
    if (!form.project_id) { setError('Please select a project.'); return; }
    if (!form.description.trim()) { setError('Description of issue is required.'); return; }
    setSaving(true);
    try {
      const reference = await generateReference(form.project_id, 'EWN', projects);
      const { data, error: insertError } = await supabase
        .from('instructions')
        .insert({
          project_id: form.project_id,
          created_by: profile.id,
          type: 'EWN',
          reference,
          date: form.date,
          time: form.time,
          location: form.location,
          gridlines: form.gridlines,
          level: form.level,
          description: form.description,
          potential_impact: form.potential_impact,
          action_requested: form.action_requested,
          status: 'draft',
        })
        .select()
        .single();
      if (insertError) throw insertError;
      if (attachments.length > 0) {
        window.__pendingUploads = window.__pendingUploads || {};
        window.__pendingUploads[data.id] = attachments;
      }
      navigate('instruction-detail', { instructionId: data.id });
    } catch (err) {
      setError(err.message || 'Failed to save EWN.');
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSave}>
      {error && <div style={S.alertError}>{error}</div>}

      <SectionCard number="1" title="Project">
        <div style={S.formGroup}>
          <label style={S.label}>Project <span style={{ color: C.danger }}>*</span></label>
          <select value={form.project_id} onChange={e => update('project_id', e.target.value)} style={S.select} required>
            <option value="">— Select project —</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.reference} — {p.name}</option>)}
          </select>
        </div>
      </SectionCard>

      <SectionCard number="2" title="Date, Time & Location">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
          <div style={S.formGroup}>
            <label style={S.label}>Date noticed <span style={{ color: C.danger }}>*</span></label>
            <input type="date" style={S.input} value={form.date} onChange={e => update('date', e.target.value)} required />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Time noticed</label>
            <input type="time" style={S.input} value={form.time} onChange={e => update('time', e.target.value)} />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Location</label>
            <input style={S.input} value={form.location} onChange={e => update('location', e.target.value)} placeholder="e.g. Level 3, Block B" />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Gridlines</label>
            <input style={S.input} value={form.gridlines} onChange={e => update('gridlines', e.target.value)} placeholder="e.g. A3 to B5" />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Level / Floor</label>
            <input style={S.input} value={form.level} onChange={e => update('level', e.target.value)} placeholder="e.g. Ground Floor" />
          </div>
        </div>
      </SectionCard>

      <SectionCard number="3" title="Description of Issue">
        <div style={S.formGroup}>
          <label style={S.label}>Describe the issue or change you have noticed <span style={{ color: C.danger }}>*</span></label>
          <textarea
            style={{ ...S.textarea, minHeight: '140px' }}
            value={form.description}
            onChange={e => update('description', e.target.value)}
            placeholder="Describe the issue clearly — include what you have observed, where, when, and why it is a problem"
            required
          />
        </div>
      </SectionCard>

      <SectionCard number="4" title="Potential Impact">
        <label style={{ ...S.label, marginBottom: '12px' }}>What could this affect?</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          {IMPACT_OPTIONS.map(opt => (
            <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', padding: '8px 14px', borderRadius: '6px', border: `1px solid ${form.potential_impact.includes(opt.value) ? '#1e40af' : C.border}`, background: form.potential_impact.includes(opt.value) ? '#dbeafe' : '#fff', fontWeight: form.potential_impact.includes(opt.value) ? '600' : '400', color: form.potential_impact.includes(opt.value) ? '#1e40af' : C.text }}>
              <input
                type="checkbox"
                checked={form.potential_impact.includes(opt.value)}
                onChange={() => toggleImpact(opt.value)}
                style={{ accentColor: '#1e40af' }}
              />
              {opt.label}
            </label>
          ))}
        </div>
      </SectionCard>

      <SectionCard number="5" title="Action Requested from Main Contractor">
        <div style={S.formGroup}>
          <label style={S.label}>What are you requesting the MC to do?</label>
          <textarea
            style={{ ...S.textarea, minHeight: '100px' }}
            value={form.action_requested}
            onChange={e => update('action_requested', e.target.value)}
            placeholder="e.g. Please issue a formal instruction to redesign the detail and confirm the revised scope in writing"
          />
        </div>
      </SectionCard>

      <SectionCard number="6" title="Raised By">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div style={S.formGroup}>
            <label style={S.label}>Name</label>
            <input style={S.input} value={form.raised_by_name} onChange={e => update('raised_by_name', e.target.value)} placeholder="Your name" />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Role / Position</label>
            <input style={S.input} value={form.raised_by_role} onChange={e => update('raised_by_role', e.target.value)} placeholder="e.g. QS, Site Manager" />
          </div>
        </div>
      </SectionCard>

      <SectionCard number="7" title="Attachments (optional)">
        <p style={{ fontSize: '13px', color: C.textMuted, margin: '0 0 14px 0' }}>
          Attach photos, drawings or documents showing the issue.
        </p>
        <DropZone
          onFilesSelected={files => setAttachments(prev => {
            const existing = new Set(prev.map(f => f.name));
            return [...prev, ...files.filter(f => !existing.has(f.name))];
          })}
          files={attachments}
          onRemove={i => setAttachments(prev => prev.filter((_, j) => j !== i))}
          accept=".pdf,.doc,.docx,.msg,.eml,.jpg,.jpeg,.png,.xlsx,.xls"
          multiple
          label="issue photos and documents"
        />
      </SectionCard>

      <LegalPreview wording={EWN_WORDING} />

      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
        <button type="button" onClick={() => navigate('projects', {})} style={{ ...S.btn, ...S.btnGhost }}>Cancel</button>
        <button type="submit" disabled={saving} style={{ ...S.btn, ...S.btnPrimary, ...S.btnLg, opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Saving…' : 'Save EWN'}
        </button>
      </div>
    </form>
  );
}

// ─── CVI FORM ─────────────────────────────────────────────────────────────────

function CVIForm({ navigate, params, profile, projects }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [attachments, setAttachments] = useState([]);

  const selectedProject = projects.find(p => p.id === (params?.projectId || ''));

  const [form, setForm] = useState({
    project_id: params?.projectId || '',
    verbal_date: new Date().toISOString().split('T')[0],
    verbal_time: '',
    verbal_method: 'Site meeting',
    instructed_by_name: '',
    instructed_by_role: '',
    instructed_by_company: selectedProject?.main_contractor || '',
    description: '',
    raised_by_name: profile?.full_name || '',
    raised_by_role: '',
  });

  function update(field, value) { setForm(f => ({ ...f, [field]: value })); }

  useEffect(() => {
    if (form.project_id) {
      const proj = projects.find(p => p.id === form.project_id);
      if (proj?.main_contractor && !form.instructed_by_company) {
        update('instructed_by_company', proj.main_contractor);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.project_id, projects]);

  async function handleSave(e) {
    e.preventDefault();
    setError('');
    if (!form.project_id) { setError('Please select a project.'); return; }
    if (!form.verbal_date) { setError('Date of verbal instruction is required.'); return; }
    if (!form.description.trim()) { setError('Description of verbal instruction is required.'); return; }
    setSaving(true);
    try {
      const reference = await generateReference(form.project_id, 'CVI', projects);
      const { data, error: insertError } = await supabase
        .from('instructions')
        .insert({
          project_id: form.project_id,
          created_by: profile.id,
          type: 'CVI',
          reference,
          date: form.verbal_date,
          time: form.verbal_time || null,
          verbal_date: form.verbal_date,
          verbal_time: form.verbal_time || null,
          verbal_method: form.verbal_method,
          instructed_by_name: form.instructed_by_name,
          instructed_by_role: form.instructed_by_role,
          description: form.description,
          status: 'draft',
        })
        .select()
        .single();
      if (insertError) throw insertError;
      if (attachments.length > 0) {
        window.__pendingUploads = window.__pendingUploads || {};
        window.__pendingUploads[data.id] = attachments;
      }
      navigate('instruction-detail', { instructionId: data.id });
    } catch (err) {
      setError(err.message || 'Failed to save CVI.');
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSave}>
      {error && <div style={S.alertError}>{error}</div>}

      <SectionCard number="1" title="Project">
        <div style={S.formGroup}>
          <label style={S.label}>Project <span style={{ color: C.danger }}>*</span></label>
          <select value={form.project_id} onChange={e => update('project_id', e.target.value)} style={S.select} required>
            <option value="">— Select project —</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.reference} — {p.name}</option>)}
          </select>
        </div>
      </SectionCard>

      <SectionCard number="2" title="Details of Verbal Instruction">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
          <div style={S.formGroup}>
            <label style={S.label}>Date of verbal instruction <span style={{ color: C.danger }}>*</span></label>
            <input type="date" style={S.input} value={form.verbal_date} onChange={e => update('verbal_date', e.target.value)} required />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Time of verbal instruction</label>
            <input type="time" style={S.input} value={form.verbal_time} onChange={e => update('verbal_time', e.target.value)} />
          </div>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <label style={{ ...S.label, marginBottom: '10px' }}>How was it given?</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {CVI_METHODS.map(m => (
              <label key={m.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', padding: '7px 14px', borderRadius: '6px', border: `1px solid ${form.verbal_method === m.value ? '#9d174d' : C.border}`, background: form.verbal_method === m.value ? '#fce7f3' : '#fff', fontWeight: form.verbal_method === m.value ? '600' : '400', color: form.verbal_method === m.value ? '#9d174d' : C.text }}>
                <input type="radio" name="verbal_method" value={m.value} checked={form.verbal_method === m.value} onChange={() => update('verbal_method', m.value)} style={{ accentColor: '#9d174d' }} />
                {m.label}
              </label>
            ))}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
          <div style={S.formGroup}>
            <label style={S.label}>Name of person who gave instruction <span style={{ color: C.danger }}>*</span></label>
            <input style={S.input} value={form.instructed_by_name} onChange={e => update('instructed_by_name', e.target.value)} placeholder="e.g. John Smith" required />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Their role / position</label>
            <input style={S.input} value={form.instructed_by_role} onChange={e => update('instructed_by_role', e.target.value)} placeholder="e.g. Site Manager" />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Their company</label>
            <input style={S.input} value={form.instructed_by_company} onChange={e => update('instructed_by_company', e.target.value)} placeholder="Main contractor name" />
          </div>
        </div>
      </SectionCard>

      <SectionCard number="3" title="What Were You Instructed to Do?">
        <div style={S.formGroup}>
          <label style={S.label}>Describe the verbal instruction given <span style={{ color: C.danger }}>*</span></label>
          <textarea
            style={{ ...S.textarea, minHeight: '140px' }}
            value={form.description}
            onChange={e => update('description', e.target.value)}
            placeholder="Describe exactly what you were verbally instructed to do, including any specific details given"
            required
          />
        </div>
      </SectionCard>

      <SectionCard number="4" title="Raised By">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div style={S.formGroup}>
            <label style={S.label}>Name</label>
            <input style={S.input} value={form.raised_by_name} onChange={e => update('raised_by_name', e.target.value)} placeholder="Your name" />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Role / Position</label>
            <input style={S.input} value={form.raised_by_role} onChange={e => update('raised_by_role', e.target.value)} placeholder="e.g. QS, Site Manager" />
          </div>
        </div>
      </SectionCard>

      <SectionCard number="5" title="Attachments (optional)">
        <p style={{ fontSize: '13px', color: C.textMuted, margin: '0 0 14px 0' }}>
          Attach any supporting documents (site diary, notes, sketches).
        </p>
        <DropZone
          onFilesSelected={files => setAttachments(prev => {
            const existing = new Set(prev.map(f => f.name));
            return [...prev, ...files.filter(f => !existing.has(f.name))];
          })}
          files={attachments}
          onRemove={i => setAttachments(prev => prev.filter((_, j) => j !== i))}
          accept=".pdf,.doc,.docx,.msg,.eml,.jpg,.jpeg,.png,.xlsx,.xls"
          multiple
          label="supporting documents"
        />
      </SectionCard>

      <LegalPreview wording={CVI_WORDING} />

      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
        <button type="button" onClick={() => navigate('projects', {})} style={{ ...S.btn, ...S.btnGhost }}>Cancel</button>
        <button type="submit" disabled={saving} style={{ ...S.btn, ...S.btnPrimary, ...S.btnLg, opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Saving…' : 'Save CVI'}
        </button>
      </div>
    </form>
  );
}

// ─── IR FORM ──────────────────────────────────────────────────────────────────

function IRForm({ navigate, params, profile, projects }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [attachments, setAttachments] = useState([]);

  const followUpFromId = params?.followUpFromId || null;
  const followUpFromRef = params?.followUpFromRef || null;
  const followUpFromType = params?.followUpFromType || null;
  const linkedEwnCviId = params?.linkedEwnCviId || null;
  const [linkedEwnCviRef, setLinkedEwnCviRef] = useState('');

  useEffect(() => {
    if (!linkedEwnCviId) return;
    supabase.from('instructions').select('reference').eq('id', linkedEwnCviId).single()
      .then(({ data }) => { if (data) setLinkedEwnCviRef(data.reference); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedEwnCviId]);

  const defaultDescription = followUpFromId
    ? `This IR is raised in response to ${followUpFromType} ${followUpFromRef}.\n\n`
    : '';

  const [form, setForm] = useState({
    project_id: params?.projectId || '',
    date: new Date().toISOString().split('T')[0],
    time: '',
    location: '',
    gridlines: '',
    level: '',
    instruction_method: 'Email',
    instructed_by_name: '',
    instructed_by_role: '',
    description: defaultDescription,
    reason_outside_scope: '',
  });

  function update(field, value) { setForm(f => ({ ...f, [field]: value })); }

  async function handleSave(e) {
    e.preventDefault();
    setError('');
    if (!form.project_id) { setError('Please select a project.'); return; }
    if (!form.date) { setError('Date of instruction is required.'); return; }
    if (!form.description.trim()) { setError('Description is required.'); return; }
    setSaving(true);
    try {
      const reference = await generateReference(form.project_id, 'IR', projects);
      const { data, error: insertError } = await supabase
        .from('instructions')
        .insert({
          project_id: form.project_id,
          created_by: profile.id,
          type: 'IR',
          reference,
          date: form.date,
          time: form.time || null,
          location: form.location,
          gridlines: form.gridlines,
          level: form.level,
          instruction_method: form.instruction_method,
          instructed_by_name: form.instructed_by_name,
          instructed_by_role: form.instructed_by_role,
          description: form.description,
          reason_outside_scope: form.reason_outside_scope,
          status: 'open',
          parent_instruction_id: linkedEwnCviId || followUpFromId || null,
          linked_ewn_cvi_id: linkedEwnCviId || followUpFromId || null,
        })
        .select()
        .single();
      if (insertError) throw insertError;
      if (attachments.length > 0) {
        window.__pendingUploads = window.__pendingUploads || {};
        window.__pendingUploads[data.id] = attachments;
      }
      navigate('instruction-detail', { instructionId: data.id });
    } catch (err) {
      setError(err.message || 'Failed to save instruction.');
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSave}>
      {error && <div style={S.alertError}>{error}</div>}

      {followUpFromId && (
        <div style={{ ...S.alertInfo, marginBottom: '20px' }}>
          <strong>IR Follow-up</strong> — This Instruction Received is being raised in response to{' '}
          <strong>{followUpFromType} {followUpFromRef}</strong>. The two records will be linked automatically.
        </div>
      )}
      {linkedEwnCviId && (
        <div style={{ background: '#dbeafe', border: '1px solid #bfdbfe', borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', fontSize: '14px', color: '#1e40af' }}>
          <strong>Linked to:</strong> {linkedEwnCviRef || '…'}
        </div>
      )}

      <SectionCard number="1" title="Project">
        <div style={S.formGroup}>
          <label style={S.label}>Project <span style={{ color: C.danger }}>*</span></label>
          <select value={form.project_id} onChange={e => update('project_id', e.target.value)} style={S.select} required>
            <option value="">— Select project —</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.reference} — {p.name}</option>)}
          </select>
        </div>
      </SectionCard>

      <SectionCard number="2" title="Instruction Details">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', marginBottom: '16px' }}>
          <div style={S.formGroup}>
            <label style={S.label}>Date of instruction <span style={{ color: C.danger }}>*</span></label>
            <input type="date" style={S.input} value={form.date} onChange={e => update('date', e.target.value)} required />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Time</label>
            <input type="time" style={S.input} value={form.time} onChange={e => update('time', e.target.value)} />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Location</label>
            <input style={S.input} value={form.location} onChange={e => update('location', e.target.value)} placeholder="e.g. Level 3, Block B" />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Gridlines</label>
            <input style={S.input} value={form.gridlines} onChange={e => update('gridlines', e.target.value)} placeholder="e.g. A3 to B5" />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Level / Floor</label>
            <input style={S.input} value={form.level} onChange={e => update('level', e.target.value)} placeholder="e.g. Ground Floor" />
          </div>
        </div>
        <div>
          <label style={{ ...S.label, marginBottom: '10px' }}>How was it received?</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {IR_METHODS.map(m => (
              <label key={m.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', padding: '7px 14px', borderRadius: '6px', border: `1px solid ${form.instruction_method === m.value ? '#065f46' : C.border}`, background: form.instruction_method === m.value ? '#d1fae5' : '#fff', fontWeight: form.instruction_method === m.value ? '600' : '400', color: form.instruction_method === m.value ? '#065f46' : C.text }}>
                <input type="radio" name="instruction_method" value={m.value} checked={form.instruction_method === m.value} onChange={() => update('instruction_method', m.value)} style={{ accentColor: '#065f46' }} />
                {m.label}
              </label>
            ))}
          </div>
        </div>
      </SectionCard>

      <SectionCard number="3" title="Instructed By">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div style={S.formGroup}>
            <label style={S.label}>Name of MC person who issued it</label>
            <input style={S.input} value={form.instructed_by_name} onChange={e => update('instructed_by_name', e.target.value)} placeholder="e.g. John Smith" />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Their role / position</label>
            <input style={S.input} value={form.instructed_by_role} onChange={e => update('instructed_by_role', e.target.value)} placeholder="e.g. Site Manager" />
          </div>
        </div>
      </SectionCard>

      <SectionCard number="4" title="Description of Works Instructed">
        <div style={S.formGroup}>
          <label style={S.label}>Description <span style={{ color: C.danger }}>*</span></label>
          <textarea
            style={{ ...S.textarea, minHeight: '120px' }}
            value={form.description}
            onChange={e => update('description', e.target.value)}
            placeholder="Describe the works instructed"
            required
          />
        </div>
        <div style={S.formGroup}>
          <label style={S.label}>Reason Works Are Outside Subcontract Scope</label>
          <textarea
            style={{ ...S.textarea, minHeight: '80px' }}
            value={form.reason_outside_scope}
            onChange={e => update('reason_outside_scope', e.target.value)}
            placeholder="Explain why these works are outside the agreed subcontract scope"
          />
        </div>
      </SectionCard>

      <SectionCard number="5" title="Attachments">
        <div style={{ ...S.alertWarning, marginBottom: '14px', fontSize: '13px' }}>
          Attaching the original instruction document (email, drawing or document) is strongly recommended for contractual purposes — this is your evidence.
        </div>
        <DropZone
          onFilesSelected={files => setAttachments(prev => {
            const existing = new Set(prev.map(f => f.name));
            return [...prev, ...files.filter(f => !existing.has(f.name))];
          })}
          files={attachments}
          onRemove={i => setAttachments(prev => prev.filter((_, j) => j !== i))}
          accept=".pdf,.doc,.docx,.msg,.eml,.jpg,.jpeg,.png,.xlsx,.xls"
          multiple
          label="original instruction (email, drawing, document)"
        />
      </SectionCard>

      <LegalPreview wording={IR_WORDING} />

      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
        <button type="button" onClick={() => navigate('projects', {})} style={{ ...S.btn, ...S.btnGhost }}>Cancel</button>
        <button type="submit" disabled={saving} style={{ ...S.btn, ...S.btnPrimary, ...S.btnLg, opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Saving…' : 'Save Instruction'}
        </button>
      </div>
    </form>
  );
}

// ─── TYPE SELECTOR + MAIN EXPORT ─────────────────────────────────────────────

export default function InstructionForm({ navigate, params }) {
  const { profile } = useAuth();
  const projects = useProjects(profile?.id, profile?.role);

  const followUpFromId = params?.followUpFromId || null;
  const linkedEwnCviId = params?.linkedEwnCviId || null;
  const defaultType = (followUpFromId || linkedEwnCviId || params?.type === 'IR') ? 'IR' : (params?.type || 'EWN');
  const [selectedType, setSelectedType] = useState(defaultType);

  const TYPE_OPTIONS = [
    {
      value: 'EWN',
      label: 'Early Warning Notice',
      desc: 'We have spotted an issue that may affect cost, time, quality or programme. We formally notify the MC and request a written instruction.',
      color: '#1e40af', bg: '#dbeafe',
    },
    {
      value: 'CVI',
      label: 'Confirmation of Verbal Instruction',
      desc: 'The MC verbally told us to do something. We formally confirm what we were told and request a written instruction before we proceed.',
      color: '#9d174d', bg: '#fce7f3',
    },
    {
      value: 'IR',
      label: 'Instruction Received',
      desc: 'The MC has issued a formal written instruction. We record it and attach the original. No sending required.',
      color: '#065f46', bg: '#d1fae5',
    },
  ];

  return (
    <div style={S.pageContainer}>
      <div style={S.pageHeader}>
        <div>
          <button
            onClick={() => params?.projectId ? navigate('project-detail', { projectId: params.projectId }) : navigate('projects', {})}
            style={{ ...S.btn, ...S.btnGhost, ...S.btnSm, marginBottom: '8px' }}
          >
            ← Back
          </button>
          <h1 style={S.pageTitle}>
            {selectedType === 'EWN' ? 'Raise Early Warning Notice' : selectedType === 'CVI' ? 'Raise Confirmation of Verbal Instruction' : 'Record Instruction Received'}
          </h1>
          <p style={{ color: C.textMuted, fontSize: '14px', margin: '4px 0 0 0' }}>
            {selectedType === 'EWN' ? 'Formally notify the Main Contractor of an issue' : selectedType === 'CVI' ? 'Confirm a verbal instruction in writing' : 'Record a formal instruction received from the Main Contractor'}
          </p>
        </div>
      </div>

      {/* Type selector (locked for IR follow-ups) */}
      <div style={{ ...S.card, marginBottom: '24px' }}>
        <h2 style={{ ...S.sectionTitle, marginBottom: '16px', fontSize: '15px' }}>Instruction Type</h2>
        {(followUpFromId || linkedEwnCviId || params?.type === 'IR') ? (
          <div style={{ padding: '16px', border: '2px solid #065f46', borderRadius: '8px', background: '#d1fae5', display: 'inline-block' }}>
            <div style={{ fontWeight: '700', fontSize: '16px', color: '#065f46', marginBottom: '2px' }}>IR</div>
            <div style={{ fontWeight: '600', fontSize: '13px', color: C.text }}>Instruction Received</div>
            {followUpFromId && (
              <div style={{ fontSize: '12px', color: C.textMuted, marginTop: '4px' }}>
                Locked — this IR is a follow-up to {params.followUpFromType} {params.followUpFromRef}
              </div>
            )}
            {linkedEwnCviId && !followUpFromId && (
              <div style={{ fontSize: '12px', color: C.textMuted, marginTop: '4px' }}>
                Locked — recording linked instruction received
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
            {TYPE_OPTIONS.map(opt => {
              const active = selectedType === opt.value;
              return (
                <div
                  key={opt.value}
                  onClick={() => setSelectedType(opt.value)}
                  style={{
                    padding: '16px', border: `2px solid ${active ? opt.color : C.border}`,
                    borderRadius: '8px', cursor: 'pointer',
                    background: active ? opt.bg : '#fff',
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{ fontWeight: '700', fontSize: '16px', color: active ? opt.color : C.text, marginBottom: '2px' }}>{opt.value}</div>
                  <div style={{ fontWeight: '600', fontSize: '13px', color: C.text, marginBottom: '6px' }}>{opt.label}</div>
                  <div style={{ fontSize: '12px', color: C.textMuted, lineHeight: '1.5' }}>{opt.desc}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Render the appropriate form */}
      {selectedType === 'EWN' && <EWNForm navigate={navigate} params={params} profile={profile} projects={projects} />}
      {selectedType === 'CVI' && <CVIForm navigate={navigate} params={params} profile={profile} projects={projects} />}
      {selectedType === 'IR' && <IRForm navigate={navigate} params={params} profile={profile} projects={projects} />}
    </div>
  );
}
